import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useMemo, useState } from 'react';
import { Icon } from '../components/Icon.tsx';
import { EASE } from '../components/motion.tsx';
import { Avatar, Button, Chip, Empty, PageHead, Skeleton, Tabs, useToast } from '../components/ui.tsx';
import { ApiError, get, photoUrl, post } from '../lib/api.ts';
import { date, dateRange, relative } from '../lib/format.ts';
import { useLookups, useMe } from '../lib/session.ts';

interface Person {
  id: string;
  name: string;
  employeeNo: string | null;
}
interface Inbox {
  leave: { id: string; employee: Person; type: string; color: string; fromDate: string; toDate: string; days: number; paid: boolean; reason: string | null; hasDocument: boolean; needsDocument: boolean; createdAt: string }[];
  overtime: { id: string; employee: Person; date: string; fromTime: string; toTime: string; reason: string; workOrderNo: string | null; createdAt: string }[];
  corrections: { id: string; employee: Person; workDate: string; type: string; clockIn: string | null; clockOut: string | null; permitFrom: string | null; permitTo: string | null; paid: boolean; notes: string | null; createdAt: string }[];
}
interface ProfileReq {
  id: string;
  employee: { name: string; employeeNo: string | null };
  section: string;
  changes: Record<string, { from: unknown; to: unknown }>;
  reason: string | null;
  createdAt: string;
}

interface ResignationReq {
  id: string;
  employee: { id: string; name: string; employeeNo: string | null };
  effectiveOn: string;
  reason: string | null;
  professional: boolean;
  createdAt: string;
}

type Kind = 'leave' | 'overtime' | 'corrections' | 'profile' | 'resignations';
/** Sections Hearth applies itself on approval; others are approved after HR edits the record. */
const APPLIED = new Set(['personal', 'contact']);
/** Rows rendered at a time: production carries hundreds of open requests, and every row animates. */
const PAGE = 50;
const CORRECTION_LABEL: Record<string, string> = { late_in: 'Late clock-in permit', early_out: 'Early leave permit', short_leave: 'Short leave', shift_correction: 'Shift correction', missed_punch: 'Missed punch' };

function hoursBetween(a: string, b: string) {
  const m = (s: string) => Number(s.slice(0, 2)) * 60 + Number(s.slice(3, 5));
  let d = m(b) - m(a);
  if (d <= 0) d += 1440;
  return `${Math.floor(d / 60)}h${d % 60 ? ` ${d % 60}m` : ''}`;
}

export function Approvals() {
  const me = useMe().data!;
  const lookups = useLookups();
  const qc = useQueryClient();
  const toast = useToast();
  const inbox = useQuery({ queryKey: ['inbox'], queryFn: () => get<Inbox>('/api/requests/inbox') });
  const profile = useQuery({ queryKey: ['profile-requests'], queryFn: () => get<ProfileReq[]>('/api/people/requests/profile'), enabled: me.access.peopleAll });
  const resignations = useQuery({ queryKey: ['resignations-pending'], queryFn: () => get<ResignationReq[]>('/api/people/resignations/list?status=pending'), enabled: me.access.resignationManage });
  const [tab, setTab] = useState<Kind>('leave');
  const [cursor, setCursor] = useState(0);
  const [shown, setShown] = useState(PAGE);
  const [notes, setNotes] = useState<Record<string, string>>({});

  const items = useMemo(() => {
    if (tab === 'profile') return profile.data ?? [];
    // Your own resignation is decided by someone else.
    if (tab === 'resignations') return (resignations.data ?? []).filter((r) => r.employee.id !== me.employee?.id);
    return inbox.data?.[tab] ?? [];
  }, [tab, inbox.data, profile.data, resignations.data, me.employee?.id]);

  // Lookup ids in profile changes are shown by name.
  const named = (k: string, v: unknown) => {
    const list = k === 'religionId' ? lookups.data?.religions : k === 'maritalStatusId' ? lookups.data?.maritalStatuses : k === 'educationLevelId' ? lookups.data?.educationLevels : null;
    return list?.find((x) => x.id === v)?.name ?? String(v ?? '∅');
  };
  const decide = useMutation({
    mutationFn: ({ kind, id, decision }: { kind: Kind; id: string; decision: 'approved' | 'rejected' }) =>
      kind === 'profile'
        ? post<{ notApplied?: string[] }>(`/api/people/requests/profile/${id}/decide`, { decision, note: notes[id] })
        : kind === 'resignations'
          ? post(`/api/people/resignations/${id}/decide`, { decision, note: notes[id] })
          : post(`/api/requests/${kind}/${id}/decide`, { decision, note: notes[id] }),
    onSuccess: (r, v) => {
      const left = (r as { notApplied?: string[] } | undefined)?.notApplied ?? [];
      toast({ title: v.decision === 'approved' ? 'Approved' : 'Rejected', body: v.kind === 'leave' && v.decision === 'approved' ? 'Added to the roster; timesheets re-derived.' : left.length ? `Not applied automatically — edit the record: ${left.join(', ')}.` : undefined });
      qc.setQueryData<Inbox>(['inbox'], (d) => (d && v.kind !== 'profile' && v.kind !== 'resignations' ? { ...d, [v.kind]: (d[v.kind] as { id: string }[]).filter((x) => x.id !== v.id) } : d));
      if (v.kind === 'profile') qc.setQueryData<ProfileReq[]>(['profile-requests'], (d) => d?.filter((x) => x.id !== v.id));
      if (v.kind === 'resignations') qc.setQueryData<ResignationReq[]>(['resignations-pending'], (d) => d?.filter((x) => x.id !== v.id));
      void qc.invalidateQueries({ queryKey: ['inbox-count'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (e) => toast({ title: 'Couldn’t save', body: e instanceof ApiError ? e.message : undefined, tone: 'error' }),
  });

  useEffect(() => {
    setCursor(0);
    setShown(PAGE);
  }, [tab]);
  const visible = items.slice(0, shown);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).closest('input, textarea, select')) return;
      const it = items[cursor] as { id: string } | undefined;
      if (e.key === 'j') {
        setCursor((c) => Math.min(c + 1, items.length - 1));
        if (cursor + 1 >= shown) setShown((n) => n + PAGE);
      }
      if (e.key === 'k') setCursor((c) => Math.max(c - 1, 0));
      if (it && e.key === 'a') decide.mutate({ kind: tab, id: it.id, decision: 'approved' });
      if (it && e.key === 'r') decide.mutate({ kind: tab, id: it.id, decision: 'rejected' });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [items, cursor, shown, tab, decide]);

  const counts = { leave: inbox.data?.leave.length ?? 0, overtime: inbox.data?.overtime.length ?? 0, corrections: inbox.data?.corrections.length ?? 0, profile: profile.data?.length ?? 0, resignations: (resignations.data ?? []).filter((r) => r.employee.id !== me.employee?.id).length };
  const total = counts.leave + counts.overtime + counts.corrections + counts.profile + counts.resignations;

  return (
    <div>
      <PageHead
        eyebrow={`Approvals · ${total} waiting`}
        title={
          total ? (
            <>
              A few things <em>need you.</em>
            </>
          ) : (
            <>
              All clear, <em>for now.</em>
            </>
          )
        }
        lede={
          <>
            Decisions apply immediately: approved leave lands on the roster and corrections re-derive the day. Once a period’s payroll is approved, requests still waiting in it expire. You can’t approve your own requests. Use <kbd>J</kbd> <kbd>K</kbd> to move, <kbd>A</kbd> approve, <kbd>R</kbd> reject.
          </>
        }
      />
      <Tabs
        idPrefix="approvals"
        value={tab}
        onChange={setTab}
        tabs={[
          { id: 'leave', label: 'Leave', count: counts.leave },
          { id: 'overtime', label: 'Overtime', count: counts.overtime },
          { id: 'corrections', label: 'Corrections', count: counts.corrections },
          ...(me.access.peopleAll ? [{ id: 'profile' as const, label: 'Profile changes', count: counts.profile }] : []),
          ...(me.access.resignationManage ? [{ id: 'resignations' as const, label: 'Resignations', count: counts.resignations }] : []),
        ]}
      />
      <div style={{ marginTop: 8 }}>
        {inbox.isLoading ? (
          <div className="stack" style={{ marginTop: 20 }}>
            <Skeleton h={90} />
            <Skeleton h={90} />
          </div>
        ) : !items.length ? (
          <Empty title="Nothing waiting here.">New requests appear the moment someone submits them.</Empty>
        ) : (
          // Keyed by tab: switching tabs replaces the list instead of animating every old row out.
          <ul className="list-plain" key={tab}>
            <AnimatePresence initial={false}>
              {visible.map((raw, i) => {
                const it = raw as { id: string; employee: { id?: string; name: string; employeeNo: string | null }; createdAt: string };
                let what: React.ReactNode = null;
                let detail: React.ReactNode = null;
                if (tab === 'leave') {
                  const l = raw as Inbox['leave'][number];
                  what = (
                    <>
                      {l.type}, <em className="faint">{l.days} day{l.days > 1 ? 's' : ''}</em>
                    </>
                  );
                  detail = (
                    <>
                      <span className="mono">{dateRange(l.fromDate, l.toDate)}</span>
                      {!l.paid ? <Chip tone="warn">Unpaid</Chip> : null}
                      {l.hasDocument ? (
                        <a className="chip chip--file" href={`/api/requests/leave/${l.id}/attachment`} target="_blank" rel="noopener">
                          <Icon name="paperclip" width={13} height={13} /> {l.needsDocument ? 'Doctor’s letter' : 'Document'}
                        </a>
                      ) : l.needsDocument ? (
                        <Chip tone="warn">No doctor’s letter yet</Chip>
                      ) : null}
                      {l.reason ? <span className="muted">“{l.reason}”</span> : null}
                    </>
                  );
                } else if (tab === 'overtime') {
                  const o = raw as Inbox['overtime'][number];
                  what = (
                    <>
                      Overtime, <em className="faint">{hoursBetween(o.fromTime, o.toTime)}</em>
                    </>
                  );
                  detail = (
                    <>
                      <span className="mono">
                        {date(o.date, { weekday: true })} · {o.fromTime.slice(0, 5)}–{o.toTime.slice(0, 5)}
                      </span>
                      {o.workOrderNo ? <Chip>{o.workOrderNo}</Chip> : null}
                      <span className="muted">“{o.reason}”</span>
                    </>
                  );
                } else if (tab === 'corrections') {
                  const c = raw as Inbox['corrections'][number];
                  what = <>{CORRECTION_LABEL[c.type] ?? c.type}</>;
                  detail = (
                    <>
                      <span className="mono">{date(c.workDate, { weekday: true })}</span>
                      {c.clockIn ? <Chip tone="info">in {c.clockIn.slice(0, 5)}</Chip> : null}
                      {c.clockOut ? <Chip tone="info">out {c.clockOut.slice(0, 5)}</Chip> : null}
                      {c.permitFrom ? <Chip tone="info">{c.permitFrom.slice(0, 5)}–{c.permitTo?.slice(0, 5)}</Chip> : null}
                      {!c.paid ? <Chip tone="warn">Unpaid</Chip> : null}
                      {c.notes ? <span className="muted">“{c.notes}”</span> : null}
                    </>
                  );
                } else if (tab === 'resignations') {
                  const r = raw as ResignationReq;
                  what = (
                    <>
                      Resigning, <em className="faint">last day {date(r.effectiveOn, { weekday: true })}</em>
                    </>
                  );
                  detail = (
                    <>
                      {!r.professional ? <Chip tone="warn">No final payslip</Chip> : null}
                      {r.reason ? <span className="muted">“{r.reason}”</span> : null}
                    </>
                  );
                } else {
                  const p = raw as ProfileReq;
                  what = (
                    <>
                      Update <em className="faint">{p.section.replace('_', ' ')}</em> details
                    </>
                  );
                  detail = (
                    <>
                      {APPLIED.has(p.section) ? null : <Chip tone="warn">Apply by hand, then approve</Chip>}
                      {Object.entries(p.changes).map(([k, v]) => (
                        <span key={k} className="mono" style={{ fontSize: 12, overflowWrap: 'anywhere' }}>
                          {k}: <s className="faint">{v.from == null ? '∅' : named(k, v.from)}</s> → {typeof v.to === 'object' && v.to !== null ? JSON.stringify(v.to) : named(k, v.to)}
                        </span>
                      ))}
                      {p.reason ? <span className="muted">“{p.reason}”</span> : null}
                    </>
                  );
                }
                const busy = decide.isPending && decide.variables?.id === it.id;
                return (
                  <motion.li
                    key={it.id}
                    layout
                    className="approval"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: 60, transition: { duration: 0.3 } }}
                    transition={{ duration: 0.45, ease: EASE }}
                    onMouseEnter={() => setCursor(i)}
                    style={{ background: i === cursor ? 'linear-gradient(90deg, transparent, var(--bg-2) 8%, var(--bg-2) 92%, transparent)' : undefined }}
                  >
                    <Avatar name={it.employee.name} size={42} photo={it.employee.id ? photoUrl(it.employee.id) : null} />
                    <div className="stack" style={{ ['--gap' as string]: '8px', minWidth: 0 }}>
                      <div className="row" style={{ ['--gap' as string]: '10px' }}>
                        <span style={{ fontWeight: 600 }}>{it.employee.name}</span>
                        <span className="faint mono" style={{ fontSize: 11 }}>
                          {it.employee.employeeNo} · {relative(it.createdAt)}
                        </span>
                      </div>
                      <div className="approval__what">{what}</div>
                      <div className="row row--wrap" style={{ ['--gap' as string]: '10px', fontSize: 'var(--step--1)' }}>
                        {detail}
                      </div>
                      <input className="input" style={{ height: 34, maxWidth: 420 }} placeholder="Add a note (optional)" value={notes[it.id] ?? ''} onChange={(e) => setNotes((n) => ({ ...n, [it.id]: e.target.value }))} aria-label={`Note for ${it.employee.name}`} />
                    </div>
                    <div className="approval__actions">
                      <Button variant="ghost" icon="x" disabled={busy} onClick={() => decide.mutate({ kind: tab, id: it.id, decision: 'rejected' })}>
                        Reject
                      </Button>
                      <Button variant="primary" icon="check" loading={busy && decide.variables?.decision === 'approved'} disabled={busy} onClick={() => decide.mutate({ kind: tab, id: it.id, decision: 'approved' })}>
                        Approve
                      </Button>
                    </div>
                  </motion.li>
                );
              })}
            </AnimatePresence>
            {items.length > shown ? (
              <li style={{ padding: '16px 0' }}>
                <Button variant="ghost" onClick={() => setShown((n) => n + PAGE)}>
                  Show {Math.min(PAGE, items.length - shown)} more · {items.length - shown} not shown
                </Button>
              </li>
            ) : null}
          </ul>
        )}
      </div>
    </div>
  );
}
